import { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { messageErreur } from './portfolioFormat';

/* ---------------------------
   Types (reflètent PortfolioAnalysisResponse côté backend)
   --------------------------- */
export interface AllocationItem {
  label: string;
  value: number;
  percentage: number;
  [key: string]: any; // requis par le typage générique de ChartDataInput (recharts)
}

export interface PortfolioPerformanceItem {
  symbol: string;
  stock_name: string;
  weight_pct: number;
  quantity: number;
  pru: number;
  current_price: number | null;
  current_value: number;
  perf_pct: number;
  perf_ytd_pct: number | null;
  /** DEBUT_ANNEE : depuis le 31/12 ; ACHAT : titre acheté en cours d'année, perf depuis l'achat. */
  perf_ytd_base: 'DEBUT_ANNEE' | 'ACHAT' | null;
  perf_absolute: number;
  pl_day_value: number;
  pl_day_pct: number;
  pl_week_value: number;
  pl_week_pct: number;
  pl_month_value: number;
  pl_month_pct: number;
  volatility_pct: number | null;
  dividends_received: number;
  expected_annual_dividend: number;
  dividend_was_normalized: boolean;
  per: number | null;
  rendement_net_pct: number | null;
  /** Dividende annuel brut normalisé / valeur de la ligne, en %. Absent d'une réponse en cache ancienne. */
  rendement_brut_pct?: number | null;
  /** Même rendement, net de l'IRVM personnes physiques du pays de l'émetteur. */
  rendement_net_pp_pct?: number | null;
  sector: string;
  country: string;
  data_unavailable: boolean;
  data_unavailable_reason: string | null;
}

export interface PortfolioHistoryPoint {
  date: string;
  value: number;
}

export interface PortfolioAnalysisData {
  total_value: number;
  total_invested: number;
  total_profit_loss: number;
  total_profit_loss_pct: number;
  total_pl_day_value: number;
  total_pl_day_pct: number;
  total_pl_week_value: number;
  total_pl_week_pct: number;
  total_pl_month_value: number;
  total_pl_month_pct: number;
  total_pl_ytd_value: number;
  total_pl_ytd_pct: number;
  sector_allocation: AllocationItem[];
  country_allocation: AllocationItem[];
  performance_by_asset: PortfolioPerformanceItem[];
  expected_annual_dividends: number;
  portfolio_yield_pct: number;
  sharpe_like_ratio: number | null;
  volatility_method: 'portfolio_realized' | 'insufficient_data';
  portfolio_volatility_pct: number | null;
  risk_free_rate_pct: number | null;
  weighted_per: number | null;
  /** Valeur des titres (apports compris). */
  portfolio_history: PortfolioHistoryPoint[];
  /** Performance cumulée hors apports (TWR, en %). */
  performance_history: PortfolioHistoryPoint[];
  total_fees: number;
  history_days_available: number;
  /** Date de la dernière séance dont les cours sont utilisés (YYYY-MM-DD). */
  cours_au: string | null;
}

const EMPTY_DATA: PortfolioAnalysisData = {
  total_value: 0,
  total_invested: 0,
  total_profit_loss: 0,
  total_profit_loss_pct: 0,
  total_pl_day_value: 0,
  total_pl_day_pct: 0,
  total_pl_week_value: 0,
  total_pl_week_pct: 0,
  total_pl_month_value: 0,
  total_pl_month_pct: 0,
  total_pl_ytd_value: 0,
  total_pl_ytd_pct: 0,
  sector_allocation: [],
  country_allocation: [],
  performance_by_asset: [],
  expected_annual_dividends: 0,
  portfolio_yield_pct: 0,
  sharpe_like_ratio: null,
  volatility_method: 'insufficient_data',
  portfolio_volatility_pct: null,
  risk_free_rate_pct: 0,
  weighted_per: null,
  portfolio_history: [],
  performance_history: [],
  total_fees: 0,
  history_days_available: 0,
  cours_au: null,
};

/**
 * Toute l'analyse (allocation, performance, dividendes, historique) est
 * désormais calculée côté backend en un seul appel (requêtes SQL batchées,
 * réutilisation de StockDailyCache.volatilite_30j et de
 * ValuationService._get_normalized_dividend) — voir
 * api/app/portfolio_crud.py::get_portfolio_analysis. Ce hook n'est plus
 * qu'un wrapper de fetch ; l'ancien pipeline client-side (~43 requêtes,
 * YTD cassé par limit=60, biais de projection dans le graphique) a été
 * supprimé.
 */
export const usePortfolioAnalysis = () => {
  const { token } = useAuth();
  const [data, setData] = useState<PortfolioAnalysisData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/v1/portfolio/analysis`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          console.error('Analyse portefeuille : HTTP', res.status);
          throw new Error(messageErreur(res.status));
        }

        const brut: PortfolioAnalysisData = await res.json();
        // Réponse mise en cache côté serveur avant l'ajout de ces champs : valeurs neutres.
        const analysis: PortfolioAnalysisData = {
          ...brut,
          performance_history: brut.performance_history ?? [],
          portfolio_volatility_pct: brut.portfolio_volatility_pct ?? null,
          risk_free_rate_pct: brut.risk_free_rate_pct ?? null,
          cours_au: brut.cours_au ?? null,
        };
        setData(
          analysis.total_value > 0 || analysis.performance_by_asset.length > 0
            ? analysis
            : EMPTY_DATA
        );
      } catch (err) {
        console.error('Error fetching portfolio analysis:', err);
        // Erreur réseau (TypeError de fetch) : jamais son texte brut à l'écran.
        setError(
          err instanceof Error && !(err instanceof TypeError) ? err.message : messageErreur(0)
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  return { data, loading, error };
};
